export const MIN_TEXT_SIZE = 50;
export const MAX_TEXT_SIZE = 300;

/** A word's percentage of its paragraph's normal text size, never arbitrary CSS. */
export function normalizeTextSize(value: unknown): string | null {
  const input = typeof value === "number" ? `${value}%` : value;
  if (typeof input !== "string") return null;
  const match = /^(\d{1,3}(?:\.\d{1,2})?)%$/.exec(input.trim());
  if (!match) return null;
  const percent = Number(match[1]);
  if (percent < MIN_TEXT_SIZE || percent > MAX_TEXT_SIZE) return null;
  return `${Math.round(percent)}%`;
}

/**
 * Flatten imported nested sizes onto text spans. Each explicit percentage refers
 * to normal paragraph text; it must not multiply with a sized ancestor. Keeping
 * sized spans free of element children also makes repeated sanitization stable.
 */
export function flattenTextSizes(
  root: HTMLElement,
  sizes: ReadonlyMap<HTMLElement, string>,
): void {
  if (!sizes.size) return;
  const walk = (element: HTMLElement, inherited: string) => {
    const size = sizes.get(element) ?? inherited;
    const children = [...element.childNodes];
    if (
      element.tagName === "SPAN" &&
      children.length > 0 &&
      children.every((child) => child.nodeType === Node.TEXT_NODE)
    ) {
      if (size !== "100%") element.style.setProperty("font-size", size);
      return;
    }
    for (const child of children) {
      if (child.nodeType === Node.ELEMENT_NODE) {
        walk(child as HTMLElement, size);
      } else if (
        child.nodeType === Node.TEXT_NODE &&
        child.textContent &&
        size !== "100%"
      ) {
        const span = document.createElement("span");
        span.style.setProperty("font-size", size);
        child.replaceWith(span);
        span.append(child);
      }
    }
  };
  walk(root, "100%");
}
